"use client";

import { useEffect, useRef, useState } from "react";
import IconClose from "@/app/(components)/generics/Icons/IconClose";
import inputStyles from "@/app/(components)/generics/inputs/default/Input/Input.module.css";
import defaultInputStyles from "@/app/(components)/generics/inputs/styles/DefaultInput.module.css";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { searchAdminPractices } from "@/app/actions/admin/action";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import { PracticeResult } from "@/app/types/payload/response/PracticeResult";
import styles from "./PracticeNameInput.module.css";

type Props = {
  value: string;
  selectedPractice: PracticeResult | null;
  onChange: (value: string, practice: PracticeResult | null) => void;
  label?: string;
  lang?: string;
  required?: boolean;
  showRequiredLabel?: boolean;
  errorMessage?: string;
};

export default function PracticeNameInput(props: Props) {
  const {
    value,
    selectedPractice,
    onChange,
    label,
    lang,
    required,
    showRequiredLabel,
    errorMessage,
  } = props;

  const [suggestions, setSuggestions] = useState<PracticeResult[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleInput = (raw: string) => {
    onChange(raw, null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (raw.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const results = await searchAdminPractices(raw);
      setSuggestions(results);
      setOpen(results.length > 0);
    }, 280);
  };

  const handleSelect = (p: PracticeResult) => {
    onChange(p.name, p);
    setSuggestions([]);
    setOpen(false);
  };

  const handleClear = () => {
    onChange("", null);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      {label && (
        <label className={inputStyles.label}>
          {label}
          {showRequiredLabel &&
            (required ? (
              <span className={inputStyles.labelRequired}>*</span>
            ) : (
              <span className={inputStyles.labelOptional}>({T(TKey.OPTIONAL, lang)})</span>
            ))}
        </label>
      )}

      <div className={styles.inputWrapper}>
        <input
          className={`${defaultInputStyles.input} ${selectedPractice ? styles.inputSelected : ""}`}
          type="text"
          value={value}
          placeholder={T(TKey.PRACTICE_NAME, lang) as string}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          readOnly={!!selectedPractice}
        />
        {selectedPractice && (
          <button type="button" className={styles.clearBtn} onClick={handleClear}>
            <IconClose />
          </button>
        )}
      </div>

      {selectedPractice && (
        <p className={styles.selectedHint}>
          {selectedPractice.address && `${selectedPractice.address} · `}
          {selectedPractice.city} {selectedPractice.postalCode} · {selectedPractice.country}
        </p>
      )}

      {open && (
        <div className={styles.dropdown}>
          {suggestions.map((p) => (
            <button
              key={p.id}
              type="button"
              className={styles.item}
              onClick={() => handleSelect(p)}
            >
              <span className={styles.itemName}>{p.name}</span>
              <span className={styles.itemMeta}>
                {p.city}
                {p.postalCode ? ` ${p.postalCode}` : ""}
              </span>
            </button>
          ))}
        </div>
      )}

      {errorMessage && (
        <div className={defaultInputStyles.errorMessage}>
          <ErrorMessage message={errorMessage} />
        </div>
      )}
    </div>
  );
}
